<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { findNode } from '../lib/documentModel';

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  components: { type: Array, default: () => [] },
  selectedId: { type: String, default: null },
});
const emit = defineEmits(['select', 'insert', 'move', 'remove']);

const { t } = useI18n();

const showAddMenu = ref(false);
const collapsed = ref({});
const dragId = ref(null);
const dropTarget = ref(null);

const ICON_MAP = {
  'cover-photo': 'i-lucide-image',
  hero: 'i-lucide-image',
  'key-info': 'i-lucide-info',
  'event-description': 'i-lucide-align-left',
  'event-schedule': 'i-lucide-calendar',
  'event-floorplans': 'i-lucide-map',
  'pricing-table': 'i-lucide-receipt',
  'event-quote': 'i-lucide-scroll-text',
  'accept-quote': 'i-lucide-check-square',
  'attributes-panel': 'i-lucide-list',
  'attributes-form': 'i-lucide-pen-line',
  'section-divider': 'i-lucide-minus',
  'content-heading': 'i-lucide-heading',
  'content-text': 'i-lucide-type',
  'content-image': 'i-lucide-image-plus',
  'content-button': 'i-lucide-mouse-pointer-click',
  'event-manager': 'i-lucide-handshake',
  'portal-tabs': 'i-lucide-layout-panel-top',
  'portal-tab': 'i-lucide-panel-top',
  'event-documents': 'i-lucide-file-text',
  'portal-section': 'i-lucide-square',
  columns: 'i-lucide-columns-2',
  column: 'i-lucide-rectangle-vertical',
  html: 'i-lucide-code',
  wrapper: 'i-lucide-box',
};

const CONTAINER_KINDS = new Set(['section', 'column', 'tab', 'wrapper']);

const componentDefs = computed(() => {
  const map = {};
  props.components.forEach(c => {
    map[c.tag_name] = c;
  });
  return map;
});

const systemComponents = computed(() =>
  props.components.filter(c => c.is_system)
);

const getIcon = tagName => ICON_MAP[tagName] || 'i-lucide-box';
const getLabel = tagName => componentDefs.value[tagName]?.label || tagName;

const nodeMeta = node => {
  if (node.kind === 'section') {
    return {
      label: 'Section',
      icon: getIcon('portal-section'),
      selectable: true,
      container: true,
    };
  }
  if (node.kind === 'columns') {
    return {
      label: 'Columns',
      icon: getIcon('columns'),
      selectable: true,
      container: false,
    };
  }
  if (node.kind === 'column') {
    return {
      label: node.span != null ? `Column · ${node.span}/12` : 'Column',
      icon: getIcon('column'),
      selectable: true,
      container: true,
    };
  }
  if (node.kind === 'tabs') {
    return {
      label: getLabel('portal-tabs'),
      icon: getIcon('portal-tabs'),
      selectable: true,
      container: false,
    };
  }
  if (node.kind === 'tab') {
    return {
      label: node.props?.name || getLabel('portal-tab'),
      icon: getIcon('portal-tab'),
      selectable: true,
      container: true,
    };
  }
  if (node.kind === 'wrapper') {
    return {
      label: node.tagName || 'wrapper',
      icon: getIcon('wrapper'),
      selectable: false,
      container: true,
    };
  }
  if (node.kind === 'html') {
    return {
      label: 'HTML',
      icon: getIcon('html'),
      selectable: true,
      container: false,
    };
  }
  return {
    label: getLabel(node.tagName),
    icon: getIcon(node.tagName),
    selectable: true,
    container: false,
  };
};

const flatNodes = computed(() => {
  const out = [];
  const walk = (list, depth, parentId) => {
    (list || []).forEach((node, index) => {
      const meta = nodeMeta(node);
      const kids = node.children || [];
      const hasKids = kids.length > 0;
      out.push({
        id: node.id,
        kind: node.kind,
        depth,
        hasKids,
        label: meta.label,
        icon: meta.icon,
        selectable: meta.selectable,
        container: meta.container,
        parentId: parentId ?? null,
        index,
        draggable: meta.selectable && node.kind !== 'column',
      });
      if (hasKids && !collapsed.value[node.id]) {
        walk(kids, depth + 1, node.id);
      }
    });
  };
  walk(props.nodes, 0, null);
  return out;
});

const isSelected = node => props.selectedId === node.id;

const toggleExpand = (node, e) => {
  e.stopPropagation();
  collapsed.value = {
    ...collapsed.value,
    [node.id]: !collapsed.value[node.id],
  };
};

const onNodeClick = node => {
  if (!node.selectable) return;
  emit('select', node.id);
};

const insertComponent = tag => {
  emit('insert', tag);
  showAddMenu.value = false;
};

const depthPadClass = depth => {
  if (depth <= 0) return 'pl-1';
  if (depth === 1) return 'pl-4';
  if (depth === 2) return 'pl-7';
  if (depth === 3) return 'pl-10';
  return 'pl-12';
};

const canAcceptDrop = (targetNode, position) => {
  if (!dragId.value || dragId.value === targetNode.id) return false;

  const dragged = findNode(props.nodes, dragId.value);
  if (!dragged) return false;

  if (position === 'inside') {
    return CONTAINER_KINDS.has(targetNode.kind);
  }
  return true;
};

const onDragStart = (e, node) => {
  if (!node.draggable) {
    e.preventDefault();
    return;
  }
  dragId.value = node.id;
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', node.id);
  emit('select', node.id);
};

const onDragOver = (e, node) => {
  if (!dragId.value || dragId.value === node.id) return;
  e.preventDefault();

  const rect = e.currentTarget.getBoundingClientRect();
  const y = e.clientY - rect.top;
  const fraction = y / rect.height;

  let position;
  if (node.container && fraction > 0.25 && fraction < 0.75) {
    position = 'inside';
  } else if (fraction < 0.5) {
    position = 'before';
  } else {
    position = 'after';
  }

  if (!canAcceptDrop(node, position)) {
    if (position === 'inside') {
      position = fraction < 0.5 ? 'before' : 'after';
    }
    if (!canAcceptDrop(node, position)) {
      dropTarget.value = null;
      return;
    }
  }

  dropTarget.value = { id: node.id, position };
};

const onDragLeave = e => {
  if (e.currentTarget.contains(e.relatedTarget)) return;
  dropTarget.value = null;
};

const resolveDropTarget = () => {
  if (!dropTarget.value || !dragId.value) return null;
  const target = dropTarget.value;
  const flatNode = flatNodes.value.find(n => n.id === target.id);
  if (!flatNode) return null;

  if (target.position === 'inside') {
    return { parentId: flatNode.id, index: 0 };
  }
  if (target.position === 'before') {
    return { parentId: flatNode.parentId, index: flatNode.index };
  }
  return { parentId: flatNode.parentId, index: flatNode.index + 1 };
};

const onDrop = e => {
  e.preventDefault();
  const resolved = resolveDropTarget();
  if (resolved && dragId.value) {
    emit('move', { id: dragId.value, target: resolved });
  }
  dragId.value = null;
  dropTarget.value = null;
};

const onDragEnd = () => {
  dragId.value = null;
  dropTarget.value = null;
};

const dropIndicatorPosition = node => {
  if (!dropTarget.value || dropTarget.value.id !== node.id) return null;
  return dropTarget.value.position;
};

const onKeyDown = (e, node) => {
  if (e.key === 'Delete' || e.key === 'Backspace') {
    if (node.selectable && node.kind !== 'column') {
      e.preventDefault();
      emit('remove', node.id);
    }
  }
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="px-3 pt-3 pb-2">
      <div class="relative">
        <button
          type="button"
          class="flex items-center justify-center gap-1.5 w-full px-2.5 py-2 rounded-lg text-xs font-medium text-white bg-n-brand-9 hover:opacity-90 transition-opacity"
          @click="showAddMenu = !showAddMenu"
        >
          <span class="i-lucide-plus size-3.5" />
          Add element
        </button>
        <div
          v-if="showAddMenu"
          class="absolute left-0 right-0 top-full mt-1 z-40 max-h-64 overflow-y-auto bg-white border border-n-slate-5 rounded-xl shadow-lg p-1.5 space-y-0.5"
        >
          <button
            v-for="comp in systemComponents"
            :key="comp.tag_name"
            type="button"
            class="flex items-center gap-2 w-full px-2.5 py-2 rounded-lg text-left text-xs text-n-slate-11 hover:bg-n-brand-2 hover:text-n-brand-11 transition-colors"
            @click="insertComponent(comp.tag_name)"
          >
            <span
              class="flex items-center justify-center size-6 rounded-md bg-n-slate-2"
            >
              <span :class="getIcon(comp.tag_name)" class="size-3.5" />
            </span>
            <span class="truncate font-medium">{{
              comp.label || comp.tag_name
            }}</span>
          </button>
          <p
            v-if="!systemComponents.length"
            class="text-xs text-n-slate-9 px-2 py-2"
          >
            No components loaded
          </p>
        </div>
      </div>
    </div>

    <div
      class="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5"
      @dragover.prevent
      @drop="onDrop"
    >
      <div
        v-for="node in flatNodes"
        :key="node.id"
        class="relative"
        :draggable="node.draggable"
        @dragstart="e => onDragStart(e, node)"
        @dragover="e => onDragOver(e, node)"
        @dragleave="onDragLeave"
        @dragend="onDragEnd"
      >
        <div
          v-if="dropIndicatorPosition(node) === 'before'"
          class="absolute -top-px left-2 right-2 h-0.5 rounded-full bg-n-brand-9 z-10"
        />

        <button
          type="button"
          class="flex items-center gap-1 w-full py-1.5 pr-2 rounded-lg text-left transition-colors"
          :class="[
            depthPadClass(node.depth),
            isSelected(node)
              ? 'bg-[#3A96DD]/10 text-[#3A96DD]'
              : 'text-n-slate-11 hover:bg-n-slate-2',
            !node.selectable ? 'cursor-default' : '',
            node.draggable ? 'cursor-grab active:cursor-grabbing' : '',
            dragId === node.id ? 'opacity-40' : '',
            dropIndicatorPosition(node) === 'inside'
              ? 'ring-2 ring-n-brand-7 ring-inset rounded-lg'
              : '',
          ]"
          @click="onNodeClick(node)"
          @keydown="e => onKeyDown(e, node)"
        >
          <span
            class="flex items-center justify-center size-5 shrink-0 rounded"
            :class="
              node.hasKids
                ? 'hover:bg-n-slate-3 text-n-slate-9'
                : 'text-transparent'
            "
            @click="node.hasKids ? toggleExpand(node, $event) : undefined"
          >
            <span
              v-if="node.hasKids"
              class="i-lucide-chevron-right size-3.5 transition-transform"
              :class="collapsed[node.id] ? '' : 'rotate-90'"
            />
          </span>

          <span
            class="flex items-center justify-center size-6 rounded-md shrink-0"
            :class="isSelected(node) ? 'bg-[#3A96DD]/20' : 'bg-n-slate-2'"
          >
            <span :class="node.icon" class="size-3.5" />
          </span>

          <span class="text-xs font-medium truncate flex-1">{{
            node.label
          }}</span>

          <span
            v-if="node.draggable"
            class="i-lucide-grip-vertical size-3 text-n-slate-7 shrink-0 opacity-0 group-hover:opacity-100"
          />
        </button>

        <div
          v-if="dropIndicatorPosition(node) === 'after'"
          class="absolute -bottom-px left-2 right-2 h-0.5 rounded-full bg-n-brand-9 z-10"
        />
      </div>

      <p
        v-if="!flatNodes.length"
        class="text-xs text-n-slate-9 px-2 py-6 text-center"
      >
        No layers yet — add an element to start
      </p>
    </div>
  </div>
</template>
